import type { BootstrapResponse } from '../types';

type Service = BootstrapResponse['services'][number];

/**
 * D78 — what an analytics or ad-platform payload may say about a service.
 *
 * FireTrack and the GTM dataLayer feed Google and Meta. A service that
 * reveals a health condition (GLP-1 weight loss, a prescription) must not
 * reach them by name — and its price can name it as surely as its title
 * ($195 is one specific treatment) — so a flagged service is reported as a
 * generic "Special service" with no id, category or value. Everything
 * else is reported exactly as before.
 */
export const SENSITIVE_SERVICE_LABEL = 'Special service';

export function isSensitive(service: Service | undefined | null): boolean {
  return service?.sensitive === true;
}

export function serviceAnalyticsFields(
  serviceId: string,
  service: Service | undefined | null,
  opts: { withValue?: boolean } = {},
): {
  service_id?: string;
  service_name?: string;
  value?: number;
  currency?: string;
} {
  if (isSensitive(service)) return { service_name: SENSITIVE_SERVICE_LABEL };
  return {
    service_id: serviceId,
    service_name: service?.name,
    ...(opts.withValue
      ? {
          value: service ? service.price.amountCents / 100 : undefined,
          currency: service?.price.currency,
        }
      : {}),
  };
}

/**
 * True when ANY line of the cart is a flagged service — an add-on can be
 * the sensitive one under an ordinary base service, and a deposit or total
 * then identifies it as surely as its name.
 */
export function cartIsSensitive(
  cart: { lines: ReadonlyArray<{ serviceId: string }> } | null | undefined,
  services: ReadonlyArray<Service> | undefined,
): boolean {
  if (!cart || !services) return false;
  const flagged = new Set(services.filter((s) => s.sensitive === true).map((s) => s.id));
  return cart.lines.some((l) => flagged.has(l.serviceId));
}
